import React from 'react';

import { useTranslation } from 'react-i18next';
import { Edge, SafeAreaView } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    NO_NETWORK_CONNECTION_DESCRIPTION_ID,
    NO_NETWORK_CONNECTION_TITLE_ID,
    NO_NETWORK_CONNECTION_TRY_AGAIN_BUTTON_ID,
} from 'config/testID';

import { SCREEN } from 'router/screens.ts';

import { selectLayoutConfig } from 'store/config/selectors';

import { COLOR } from 'utils/colors';
import useSafeAreaEdges from 'utils/hooks/useSafeAreaEdges';
import { navigationRef } from 'utils/navigation/navigationUtils.ts';
import { isTablet } from 'utils/responsiveness';

import DialogView from 'components/Dialog';
import DialogBody from 'components/Dialog/components/DialogBody';
import useInternetGetBack from 'components/NoInternetAlert/hooks/useInternetGetBack';
import useNoInternetAlertVisible from 'components/NoInternetAlert/hooks/useNoInternetAlertVisible';

import styles from './styles';

const {
    NO_NETWORK_CONNECTION: {
        TRY_AGAIN_BUTTON,
        SCREEN_ALERT_NO_NETWORK_CONNECTION_TITLE,
        SCREEN_ALERT_NO_NETWORK_CONNECTION_DESCRIPTION,
    },
} = LANGUAGE_KEYS;

export default function NoInternetAlert({
    isModalNotification = false,
    isPlayerNotification = false,
    supportedOrientation,
}: {
    isModalNotification?: boolean;
    isPlayerNotification?: boolean;
    supportedOrientation?: Array<
        | 'portrait'
        | 'portrait-upside-down'
        | 'landscape'
        | 'landscape-left'
        | 'landscape-right'
    >;
}) {
    const { visible } = useNoInternetAlertVisible(
        isPlayerNotification || isModalNotification,
    );
    const { tryAgain } = useInternetGetBack(
        isPlayerNotification ||
            isModalNotification ||
            navigationRef.current?.getCurrentRoute()?.name === SCREEN.EPG,
    );

    const { primary } = useSelector(selectLayoutConfig);
    const { t } = useTranslation();
    const edges: Edge[] = useSafeAreaEdges();
    const title = t(SCREEN_ALERT_NO_NETWORK_CONNECTION_TITLE, {
        defaultValue: SCREEN_ALERT_NO_NETWORK_CONNECTION_TITLE,
    });

    if ((visible && isModalNotification) || (isPlayerNotification && visible)) {
        return (
            <SafeAreaView
                style={styles.playerNotificationContainer}
                edges={edges}
            >
                <DialogBody
                    title={title.replace(/\r?\n/g, '')}
                    titleTestID={NO_NETWORK_CONNECTION_TITLE_ID}
                    description={SCREEN_ALERT_NO_NETWORK_CONNECTION_DESCRIPTION}
                    descriptionTestID={NO_NETWORK_CONNECTION_DESCRIPTION_ID}
                    actionButtonTitle={TRY_AGAIN_BUTTON}
                    handleActionButtonPress={tryAgain}
                    actionButtonFillColor={primary || COLOR.primary}
                    actionButtonStyle={styles.button}
                    actionButtonTextStyle={styles.buttonText}
                    actionButtonTestID={
                        NO_NETWORK_CONNECTION_TRY_AGAIN_BUTTON_ID
                    }
                />
            </SafeAreaView>
        );
    }

    if (visible && !isPlayerNotification) {
        return (
            <SafeAreaView edges={edges}>
                <DialogView
                    visible
                    title={isTablet ? title.replace(/\r?\n/g, '') : title}
                    titleTestID={NO_NETWORK_CONNECTION_TITLE_ID}
                    description={SCREEN_ALERT_NO_NETWORK_CONNECTION_DESCRIPTION}
                    actionButtonTitle={TRY_AGAIN_BUTTON}
                    descriptionTestID={NO_NETWORK_CONNECTION_DESCRIPTION_ID}
                    actionButtonOnPress={tryAgain}
                    actionButtonFillColor={primary || COLOR.primary}
                    actionButtonStyle={styles.button}
                    actionButtonTextStyle={styles.buttonText}
                    supportedOrientation={supportedOrientation}
                    actionButtonTestID={
                        NO_NETWORK_CONNECTION_TRY_AGAIN_BUTTON_ID
                    }
                />
            </SafeAreaView>
        );
    }

    return null;
}
